import classNames from "classnames";
import {
  ComponentPropsWithoutRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  useTransitionState,
  type TransitionStatus,
} from "react-transition-state";

import { Button, Text } from "@src/straps/base";
import Icon from "@src/straps/base/icons/Icon/Icon";

type NotifierProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly titleText: string;
  readonly titleIcon: ComponentPropsWithoutRef<typeof Icon>["name"];
  readonly disableMinimize?: boolean;
  readonly containerTestId?: string;
  readonly contentTestId?: string;
  readonly width?: number;
  readonly height?: number;
};

// Opens a notification pop up from the bottom of the screen used to keep track of long-running tasks.
export const Notifier = ({
  open,
  onClose,
  titleText,
  titleIcon,
  disableMinimize,
  containerTestId,
  contentTestId,
  width = 240,
  height = 256,
  children,
}: PropsWithChildren<NotifierProps>) => {
  const [minimized, setMinimized] = useState(false);

  const [transitionState, toggle] = useTransitionState({
    timeout: 500,
    mountOnEnter: true,
    unmountOnExit: true,
    preEnter: true,
  });

  useEffect(() => {
    toggle(open);
  }, [open, toggle]);

  const _onClose = useCallback(() => {
    onClose();
    setTimeout(() => setMinimized(false), 500);
  }, [onClose]);

  useEffect(() => {
    if (disableMinimize && minimized) {
      setMinimized(false);
    }
  }, [disableMinimize, minimized]);

  const onMinimize = useCallback(() => setMinimized(!minimized), [minimized]);

  const statusClasses: Partial<Record<TransitionStatus, string>> = {
    preEnter: "translate-y-full",
    entering: "translate-y-0 transition-transform duration-500",
    entered: "translate-y-0",
    exiting: "translate-y-full transition-transform duration-500",
  };

  return transitionState.isMounted ? (
    <div
      data-testid={containerTestId}
      className={classNames(
        "fixed bottom-0 right-[100px] z-[100000] shadow-lg",
        statusClasses[transitionState.status]
      )}
      style={{ width }}
    >
      <div className="flex h-15 items-center justify-between bg-straps-primary-hover px-5">
        <div className="flex items-center justify-between">
          <Icon
            name={titleIcon}
            size="extraLarge"
            className="text-pure-white"
          />
          <Text className="pl-3.5 font-bold" color="white">
            {titleText}
          </Text>
        </div>
        {!disableMinimize ? (
          <Button
            aria-label="Minimize"
            dataTestId="minimize-button"
            icon="caret-down"
            noFrameVariant="icon"
            color="white"
            className={classNames("text-straps-tertiary transition-transform", {
              "rotate-180": minimized,
            })}
            onClick={onMinimize}
          />
        ) : (
          <Button
            aria-label="Close"
            dataTestId="close-button"
            icon="close"
            noFrameVariant="icon"
            color="white"
            className="text-straps-tertiary"
            onClick={_onClose}
          />
        )}
      </div>
      <div
        data-testid={contentTestId}
        className={classNames("flex flex-col bg-pure-white", {
          "transition-all": open,
          "opacity-0": minimized,
          "opacity-100": !minimized,
        })}
        style={{
          height: minimized ? "0px" : `${height}px`,
        }}
      >
        {children}
      </div>
    </div>
  ) : null;
};
